import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'gatsby';
import styled from 'styled-components';
import { Icon } from '@components/icons';

/* The project card, shared by the home section and the /projects page so the
   two cannot drift apart — the same arrangement the blog list uses.

   The card carries three things: title, description, links. Everything else a
   project knows about itself (tech, company, date, tags) lives on its own
   page, because a grid of cards is for scanning and a wall of metadata is the
   opposite of scannable. */
export const ProjectGrid = styled.ul`
  ${({ theme }) => theme.mixins.resetList};
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  grid-gap: 15px;
  position: relative;
  width: 100%;

  @media (max-width: 1080px) {
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  }
`;

export const StyledProject = styled.li`
  position: relative;
  cursor: default;
  transition: var(--transition);

  /* :has(:focus-visible) rather than :focus-within. A mouse click leaves focus
     inside the card, and :focus-within cannot tell that from a keyboard tab —
     so the lifted state stayed on until you clicked elsewhere. */
  &:hover,
  &:has(:focus-visible) {
    .project-inner {
      border-color: var(--accent);
    }
  }

  @media (prefers-reduced-motion: no-preference) {
    &:hover,
    &:has(:focus-visible) {
      .project-inner {
        transform: translateY(-7px);
      }
    }
  }

  .project-inner {
    ${({ theme }) => theme.mixins.boxShadow};
    ${({ theme }) => theme.mixins.flexBetween};
    flex-direction: column;
    align-items: flex-start;
    position: relative;
    height: 100%;
    padding: 1.5rem 1.25rem 1rem 1.25rem;
    border: 1px solid var(--border);
    border-radius: var(--border-radius);
    background-color: var(--surface-transparent);
    ${({ theme }) => theme.mixins.glass};
    transition: var(--transition);
  }

  .project-title {
    margin: 0 0 var(--space-3);
    color: var(--text-emphasis);
    font-size: var(--fz-lg);
    font-weight: 500;
    transition: var(--transition);

    /* The pseudo-element covers the whole card, so anywhere that isn't one of
       the icon links opens the project page. */
    a:before {
      content: '';
      display: block;
      position: absolute;
      z-index: 0;
      inset: 0;
    }
  }

  &:hover .project-title,
  &:has(:focus-visible) .project-title {
    color: var(--accent);
  }

  .project-description {
    margin: 0;
    color: var(--text-secondary);
    font-size: var(--fz-md);
    line-height: var(--lh-body);
  }

  /* Above the title's overlay, so these reach their own destinations rather
     than being swallowed by the card link. */
  .project-links {
    display: flex;
    align-items: center;
    position: relative;
    z-index: 1;
    margin-top: var(--space-3);
    margin-left: -7px;
    color: var(--text-secondary);

    a {
      ${({ theme }) => theme.mixins.flexCenter};
      padding: 5px 7px;
      transition: var(--transition);

      &:hover,
      &:focus-visible {
        color: var(--accent);
      }

      &.external svg {
        width: 20px;
        height: 20px;
        margin-top: -2px;
      }

      svg {
        width: 18px;
        height: 18px;
      }
    }
  }
`;

/* Rendered in a fixed order so the icons stay in the same place from card to
   card; a project without a given link simply drops it. Exported because the
   featured cards render the same set — they read from the same frontmatter
   now, so there is no reason for them to disagree about it. */
export const linksFor = ({ github, external, instagram, ios, android }) =>
  [
    { name: 'GitHub', label: 'GitHub', url: github },
    { name: 'Instagram', label: 'Instagram', url: instagram },
    { name: 'External', label: 'website', url: external, className: 'external' },
    { name: 'AppStore', label: 'App Store', url: ios },
    { name: 'PlayStore', label: 'Google Play', url: android },
  ].filter(link => link.url);

/* The card shows `description`, never the markdown body. The body is the
   write-up, which belongs on the project's own page; a card that rendered it
   would grow to whatever length the write-up happens to be. */
export const ProjectCardInner = ({ project }) => {
  const { slug, title, description } = project;
  const links = linksFor(project);

  return (
    <div className="project-inner">
      <header>
        <h3 className="project-title">
          <Link to={slug}>{title}</Link>
        </h3>

        {description ? (
          <p className="project-description">{description}</p>
        ) : null}
      </header>

      {links.length > 0 && (
        <footer>
          <div className="project-links">
            {links.map(({ name, label, url, className }) => (
              <a
                key={name}
                href={url}
                className={className}
                aria-label={`${title} on ${label}`}
                target="_blank"
                rel="noreferrer">
                <Icon name={name} />
              </a>
            ))}
          </div>
        </footer>
      )}
    </div>
  );
};

const projectShape = PropTypes.shape({
  slug: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  github: PropTypes.string,
  external: PropTypes.string,
  instagram: PropTypes.string,
  ios: PropTypes.string,
  android: PropTypes.string,
});

ProjectCardInner.propTypes = {
  project: projectShape.isRequired,
};

const ProjectCard = ({ project }) => (
  <StyledProject>
    <ProjectCardInner project={project} />
  </StyledProject>
);

ProjectCard.propTypes = {
  project: projectShape.isRequired,
};

export default ProjectCard;
